import i18n from '@/i18n';

/**
 * The language the UI is shown in. Dates, numbers and relative times must use this (not the
 * browser/system locale), so Intl output and the translated sentences around it share one language.
 */
export function appLocale(): string {
  return i18n.language || i18n.resolvedLanguage || 'en';
}

export function usd(n: number | null | undefined, digits?: number): string {
  if (n == null || Number.isNaN(n)) return '—';
  const d = digits ?? (Math.abs(n) < 1 ? (Math.abs(n) < 0.01 && n !== 0 ? 4 : 3) : 2);
  return `$${n.toFixed(d)}`;
}

export function tokens(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return String(n);
}

export function pct(n: number | null | undefined): string {
  if (n == null) return '—';
  return `${(n * 100).toFixed(n >= 0.995 || n === 0 ? 0 : 1)}%`;
}

export function ms(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 60_000) return `${(n / 60_000).toFixed(1)}m`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}s`;
  return `${Math.round(n)}ms`;
}

export function relTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '—';
  const diff = (new Date(iso).getTime() - now) / 1000;
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat(appLocale(), { numeric: 'auto', style: 'narrow' });
  if (abs < 45) return rtf.format(Math.round(diff), 'second');
  if (abs < 2700) return rtf.format(Math.round(diff / 60), 'minute');
  if (abs < 79200) return rtf.format(Math.round(diff / 3600), 'hour');
  return rtf.format(Math.round(diff / 86400), 'day');
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(appLocale(), {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function timeOfDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString(appLocale());
}

export function num(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return n.toLocaleString(appLocale());
}

export function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function truncate(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

export function preview(value: unknown, n = 120): string {
  if (value == null) return '';
  if (typeof value === 'string') return truncate(value.replace(/\s+/g, ' '), n);
  try {
    return truncate(JSON.stringify(value), n);
  } catch {
    return String(value);
  }
}
